// デメリットパネルの行整形と列定義組み立てをまとめる service です。
export function createDemeritPanelService(deps) {
    const {
        PANEL_TYPE,
        CELLTYPE,
        DEMERIT_KIND_OPTIONS,
        DEMERIT_KIND_FIELD,
        DEMERIT_KIND_NAME,
        DEMERIT_KIND_HUDA,
        DEMERIT_NAME_FLG,
        DEMERIT_HUDA_FLG,
        DEMERIT_DMT_CD,
        DEMERIT_NAME_DMY,
        DEMERIT_HUDA_DMY,
        DEMERIT_UI_FIELDS,
        DEMERIT_HIDDEN_DB_FIELDS,
        isEmpty,
        DicVal,
        parseBoolLike,
        normalizeColumnIdUpper,
        extractMasterCodeFromDisplayValue,
        buildDemeritMasterSearchParam,
        getFukushizaiColumnInfoById,
        entrydataRef
    } = deps;

    // デメリット種別の入力値を画面内で扱うコードへ正規化します。
    function normalizeDemeritKindValue(value) {
        const s = String(value ?? "").trim();
        const low = s.toLowerCase();
        if (s === DEMERIT_KIND_NAME || low === "name" || s === "ネーム") return DEMERIT_KIND_NAME;
        if (s === DEMERIT_KIND_HUDA || low === "huda" || s === "札") return DEMERIT_KIND_HUDA;
        return "";
    }

    // 行上の種別保持値を取得します。
    function getDemeritKindValue(row) {
        if (isEmpty(row) || typeof row !== "object") return "";
        return normalizeDemeritKindValue(DicVal(row, DEMERIT_KIND_FIELD, ""));
    }

    // 行へ種別保持値を書き戻します。
    function setDemeritKindValue(row, value) {
        if (isEmpty(row) || typeof row !== "object") return row;
        row[DEMERIT_KIND_FIELD] = normalizeDemeritKindValue(value);
        return row;
    }

    // 大文字小文字差を吸収して行データから値を取得します。
    function demeritValue(row, columnId) {
        if (isEmpty(row) || isEmpty(columnId)) return "";
        const keys = Object.keys(row);
        const target = String(columnId ?? "").trim().toUpperCase();
        const key = keys.find((k) => String(k ?? "").trim().toUpperCase() === target);
        return isEmpty(key) ? "" : String(row[key] ?? "").trim();
    }

    // データベース上のフラグ値を真偽値として扱えるよう正規化します。
    function isDemeritFlagOn(value) {
        const s = String(value ?? "").trim().toLowerCase();
        return s === "1" || s === "true" || s === "yes";
    }

    // 画面保持値があればそれを優先し、なければ DB フラグから種別を復元します。
    function resolveDemeritKind(row) {
        const current = getDemeritKindValue(row);
        if (current === DEMERIT_KIND_NAME || current === DEMERIT_KIND_HUDA) return current;
        if (isDemeritFlagOn(demeritValue(row, DEMERIT_NAME_FLG))) return DEMERIT_KIND_NAME;
        if (isDemeritFlagOn(demeritValue(row, DEMERIT_HUDA_FLG))) return DEMERIT_KIND_HUDA;
        return "";
    }

    // 画面表示用にネーム/札のダミー列を補完します。
    function normalizeDemeritRow(row) {
        const next = { ...(row || {}) };
        const kind = resolveDemeritKind(next);
        const dmtCd = demeritValue(next, DEMERIT_DMT_CD);
        setDemeritKindValue(next, kind);
        if (kind === DEMERIT_KIND_NAME) {
            next[DEMERIT_NAME_DMY] = String(DicVal(next, DEMERIT_NAME_DMY, "") || dmtCd);
            next[DEMERIT_HUDA_DMY] = "";
        } else if (kind === DEMERIT_KIND_HUDA) {
            next[DEMERIT_HUDA_DMY] = String(DicVal(next, DEMERIT_HUDA_DMY, "") || dmtCd);
            next[DEMERIT_NAME_DMY] = "";
        } else {
            next[DEMERIT_NAME_DMY] = "";
            next[DEMERIT_HUDA_DMY] = "";
        }
        return next;
    }

    // 行配列全体を表示用に正規化します。
    function normalizeDemeritRows(rows) {
        if (!Array.isArray(rows)) return [];
        return rows.map((row) => normalizeDemeritRow(row));
    }

    // 種別変更時に DB 保存用フラグと入力列の整合を取り直します。
    function applyDemeritKindToRow(row, kind) {
        if (isEmpty(row)) return row;
        const nextKind = normalizeDemeritKindValue(kind);
        setDemeritKindValue(row, nextKind);
        if (nextKind === DEMERIT_KIND_NAME) {
            row[DEMERIT_NAME_FLG] = "1";
            row[DEMERIT_HUDA_FLG] = "0";
            row[DEMERIT_HUDA_DMY] = "";
            row[DEMERIT_DMT_CD] = extractMasterCodeFromDisplayValue(DicVal(row, DEMERIT_NAME_DMY, ""));
        } else if (nextKind === DEMERIT_KIND_HUDA) {
            row[DEMERIT_NAME_FLG] = "0";
            row[DEMERIT_HUDA_FLG] = "1";
            row[DEMERIT_NAME_DMY] = "";
            row[DEMERIT_DMT_CD] = extractMasterCodeFromDisplayValue(DicVal(row, DEMERIT_HUDA_DMY, ""));
        } else {
            row[DEMERIT_NAME_FLG] = "0";
            row[DEMERIT_HUDA_FLG] = "0";
            row[DEMERIT_NAME_DMY] = "";
            row[DEMERIT_HUDA_DMY] = "";
            row[DEMERIT_DMT_CD] = "";
        }
        return row;
    }

    // ネーム/札列の直接編集時にコード列を同期します。
    function syncDemeritDmtCd(row, changedColumnId) {
        if (isEmpty(row)) return row;
        const changed = normalizeColumnIdUpper(changedColumnId);
        if (getDemeritKindValue(row) === "") {
            if (changed === DEMERIT_NAME_DMY.toUpperCase()) applyDemeritKindToRow(row, DEMERIT_KIND_NAME);
            if (changed === DEMERIT_HUDA_DMY.toUpperCase()) applyDemeritKindToRow(row, DEMERIT_KIND_HUDA);
        }
        const kind = resolveDemeritKind(row);
        if (kind === DEMERIT_KIND_NAME) {
            row[DEMERIT_DMT_CD] = extractMasterCodeFromDisplayValue(DicVal(row, DEMERIT_NAME_DMY, ""));
            row[DEMERIT_HUDA_DMY] = "";
        } else if (kind === DEMERIT_KIND_HUDA) {
            row[DEMERIT_DMT_CD] = extractMasterCodeFromDisplayValue(DicVal(row, DEMERIT_HUDA_DMY, ""));
            row[DEMERIT_NAME_DMY] = "";
        }
        return row;
    }

    // 保存前に UI 専用列を除きつつ DB 用の値へ戻します。
    function normalizeDemeritRowForSave(row) {
        const normalized = normalizeDemeritRow(row);
        const kind = resolveDemeritKind(normalized);
        if (kind === DEMERIT_KIND_NAME) {
            normalized[DEMERIT_NAME_FLG] = "1";
            normalized[DEMERIT_HUDA_FLG] = "0";
            normalized[DEMERIT_DMT_CD] = extractMasterCodeFromDisplayValue(DicVal(normalized, DEMERIT_NAME_DMY, ""));
        } else if (kind === DEMERIT_KIND_HUDA) {
            normalized[DEMERIT_NAME_FLG] = "0";
            normalized[DEMERIT_HUDA_FLG] = "1";
            normalized[DEMERIT_DMT_CD] = extractMasterCodeFromDisplayValue(DicVal(normalized, DEMERIT_HUDA_DMY, ""));
        } else {
            normalized[DEMERIT_NAME_FLG] = "0";
            normalized[DEMERIT_HUDA_FLG] = "0";
            normalized[DEMERIT_DMT_CD] = "";
        }
        DEMERIT_UI_FIELDS.forEach((field) => {
            if (field in normalized) delete normalized[field];
        });
        return normalized;
    }

    // 種別列の表示ラベルを取得します。
    function demeritKindLabel(value, options = DEMERIT_KIND_OPTIONS) {
        const opt = (Array.isArray(options) ? options : DEMERIT_KIND_OPTIONS).find((o) => o.code === String(value ?? ""));
        return isEmpty(opt) ? "" : opt.name;
    }

    // 既存列定義から select 候補を借用します。
    function demeritKindOptionsFromColumn(sourceCol, carrierCol = null) {
        const sourceColInfo = DicVal(sourceCol, "colinfo", {});
        const carrierColInfo = DicVal(carrierCol, "colinfo", {});
        const candidates = [
            DicVal(carrierColInfo, "options", []),
            DicVal(carrierCol, "options", []),
            DicVal(sourceColInfo, "options", []),
            DicVal(sourceCol, "options", [])
        ];
        for (let i = 0; i < candidates.length; i++) {
            const options = candidates[i];
            if (Array.isArray(options) && options.length !== 0) return options;
        }
        return DEMERIT_KIND_OPTIONS;
    }

    // 種別列の datatype は既存列定義を優先して引き継ぎます。
    function demeritKindDatatypeFromColumn(sourceCol, carrierCol = null) {
        const sourceColInfo = DicVal(sourceCol, "colinfo", {});
        const carrierColInfo = DicVal(carrierCol, "colinfo", {});
        const datatype = String(
            DicVal(carrierCol, "datatype",
                DicVal(carrierColInfo, "datatype",
                    DicVal(sourceCol, "datatype", DicVal(sourceColInfo, "datatype", CELLTYPE.SELECT))
                )
            ) ?? ""
        ).trim();
        return datatype === "" ? CELLTYPE.SELECT : datatype;
    }

    // 種別列の編集可否はロック系設定をまとめて判定します。
    function isDemeritKindColumnEditable(sourceCol, params, carrierCol = null) {
        if (params?.context?.entrylock) return false;
        const sourceColInfo = DicVal(sourceCol, "colinfo", sourceCol);
        const carrierColInfo = DicVal(carrierCol, "colinfo", {});
        const locked = parseBoolLike(DicVal(carrierColInfo, "locked", DicVal(sourceColInfo, "locked", false)), false);
        const unchange = parseBoolLike(DicVal(carrierColInfo, "unchange", DicVal(sourceColInfo, "unchange", false)), false);
        return !locked && !unchange;
    }

    // ネーム用・札用の仮想マスタ列を組み立てます。
    function buildDemeritMasterColumn(columnId, label, kind, sourceCol) {
        if (isEmpty(sourceCol)) return null;
        const activeKind = kind;
        const sourceColInfo = DicVal(sourceCol, "colinfo", {});
        const sourceSearchParam = DicVal(sourceCol, "searchparam", DicVal(sourceColInfo, "searchparam", null));
        const searchparam = buildDemeritMasterSearchParam(sourceSearchParam, entrydataRef.current);
        return {
            ...sourceCol,
            headerName: label,
            field: columnId,
            colId: columnId,
            hide: false,
            lockVisible: false,
            suppressNavigable: false,
            datatype: DicVal(sourceCol, "datatype", DicVal(sourceColInfo, "datatype", CELLTYPE.MASTER)),
            dataid: DicVal(sourceCol, "dataid", DicVal(sourceColInfo, "dataid", "")),
            dataidctrl: DicVal(sourceCol, "dataidctrl", DicVal(sourceColInfo, "dataidctrl", "")),
            dbcolumn: DicVal(sourceCol, "dbcolumn", DicVal(sourceColInfo, "dbcolumn", "")),
            dbcolumnnm: DicVal(sourceCol, "dbcolumnnm", DicVal(sourceColInfo, "dbcolumnnm", "")),
            mastertype: DicVal(sourceCol, "mastertype", DicVal(sourceColInfo, "mastertype", "")),
            searchparam,
            colinfo: {
                ...sourceColInfo,
                type: PANEL_TYPE.DEMERIT,
                columnid: columnId,
                columnnm: label,
                datatype: DicVal(sourceColInfo, "datatype", DicVal(sourceCol, "datatype", CELLTYPE.MASTER)),
                dataid: DicVal(sourceColInfo, "dataid", DicVal(sourceCol, "dataid", "")),
                dataidctrl: DicVal(sourceColInfo, "dataidctrl", DicVal(sourceCol, "dataidctrl", "")),
                dbcolumn: DicVal(sourceColInfo, "dbcolumn", DicVal(sourceCol, "dbcolumn", "")),
                dbcolumnnm: DicVal(sourceColInfo, "dbcolumnnm", DicVal(sourceCol, "dbcolumnnm", "")),
                mastertype: DicVal(sourceColInfo, "mastertype", DicVal(sourceCol, "mastertype", "")),
                searchparam,
                locked: false,
                options: []
            },
            editable: function (params) {
                if (params.context?.entrylock) return false;
                return resolveDemeritKind(params.data) === activeKind;
            },
            cellStyle: function (params) {
                const active = resolveDemeritKind(params.data) === activeKind;
                return active
                    ? { backgroundColor: "#ffffff", color: "#2f3a4a" }
                    : { backgroundColor: "#eef1f1", color: "#2f3a4a" };
            }
        };
    }

    // 種別列本体は既存列設定を流用しつつ仮想列へ差し替えます。
    function buildDemeritKindColumn(sourceCol, carrierCol = null) {
        const sourceColInfo = DicVal(sourceCol, "colinfo", {});
        const carrierColInfo = DicVal(carrierCol, "colinfo", {});
        const options = demeritKindOptionsFromColumn(sourceCol, carrierCol);
        const datatype = demeritKindDatatypeFromColumn(sourceCol, carrierCol);
        const locked = parseBoolLike(DicVal(carrierColInfo, "locked", DicVal(sourceColInfo, "locked", false)), false);
        const unchange = parseBoolLike(DicVal(carrierColInfo, "unchange", DicVal(sourceColInfo, "unchange", false)), false);
        const selectSettingProps = {};
        [
            "cellEditor",
            "cellEditorParams",
            "cellRenderer",
            "cellRendererSelector",
            "valueGetter",
            "valueSetter",
            "valueParser",
            "refData",
            "searchtype"
        ].forEach((key) => {
            const borrowed = DicVal(carrierCol, key, undefined);
            if (borrowed !== undefined) selectSettingProps[key] = borrowed;
        });
        return {
            ...sourceCol,
            ...selectSettingProps,
            headerName: DicVal(sourceCol, "headerName", DicVal(sourceColInfo, "columnnm", "ネーム/札")),
            field: DEMERIT_KIND_FIELD,
            colId: DEMERIT_KIND_FIELD,
            hide: false,
            width: 110,
            editable: (params) => isDemeritKindColumnEditable(sourceCol, params, carrierCol),
            lockVisible: false,
            suppressNavigable: false,
            filter: "agSetColumnFilter",
            datatype,
            options,
            colinfo: {
                ...sourceColInfo,
                type: PANEL_TYPE.DEMERIT,
                columnid: DEMERIT_KIND_FIELD,
                columnnm: DicVal(sourceColInfo, "columnnm", "ネーム/札"),
                table: DicVal(sourceColInfo, "table", "-"),
                datatype,
                locked,
                unchange,
                saveed: parseBoolLike(DicVal(sourceColInfo, "saveed", false), false),
                primary: false,
                primarykey: false,
                dbcolumn: DicVal(sourceColInfo, "dbcolumn", ""),
                dbcolumnnm: DicVal(sourceColInfo, "dbcolumnnm", ""),
                dataid: DicVal(sourceColInfo, "dataid", ""),
                dataidctrl: DicVal(sourceColInfo, "dataidctrl", ""),
                searchtype: DicVal(carrierColInfo, "searchtype", DicVal(sourceColInfo, "searchtype", "")),
                options
            },
            valueFormatter: (params) => demeritKindLabel(params.value, options)
        };
    }

    // 元列定義から UI 専用の 3 列構成へ差し替えます。
    function buildDemeritGridColumns(colDef) {
        if (!Array.isArray(colDef)) return colDef;
        const hiddenIds = new Set(DEMERIT_HIDDEN_DB_FIELDS.map((id) => id.toUpperCase()));
        const findSourceColumn = (columnId) => {
            const target = normalizeColumnIdUpper(columnId);
            const existing = colDef.find((c) => normalizeColumnIdUpper(DicVal(c, "colId", DicVal(c, "field", ""))) === target);
            if (!isEmpty(existing)) return existing;
            const colinfo = getFukushizaiColumnInfoById(columnId);
            if (!isEmpty(colinfo)) {
                return {
                    headerName: String(DicVal(colinfo, "columnnm", columnId) ?? columnId),
                    field: columnId,
                    colId: columnId,
                    datatype: DicVal(colinfo, "datatype", ""),
                    dataid: DicVal(colinfo, "dataid", ""),
                    dataidctrl: DicVal(colinfo, "dataidctrl", ""),
                    dbcolumn: DicVal(colinfo, "dbcolumn", ""),
                    dbcolumnnm: DicVal(colinfo, "dbcolumnnm", ""),
                    mastertype: DicVal(colinfo, "mastertype", ""),
                    searchtype: DicVal(colinfo, "searchtype", ""),
                    searchparam: buildDemeritMasterSearchParam(DicVal(colinfo, "searchparam", null), entrydataRef.current),
                    colinfo
                };
            }
            return {
                headerName: columnId,
                field: columnId,
                colId: columnId
            };
        };
        const sourceKind = findSourceColumn(DEMERIT_KIND_FIELD);
        const carrierKind = findSourceColumn(DEMERIT_NAME_FLG);
        const sourceName = findSourceColumn(DEMERIT_NAME_DMY);
        const sourceHuda = findSourceColumn(DEMERIT_HUDA_DMY);
        let insertIndex = colDef.findIndex((c) => hiddenIds.has(String(c.colId ?? "").toUpperCase()));
        if (insertIndex === -1) insertIndex = colDef.length;
        const base = colDef.filter((c) => {
            const id = String(c.colId ?? "").toUpperCase();
            if (hiddenIds.has(id)) return false;
            if (id === DEMERIT_KIND_FIELD.toUpperCase()) return false;
            if (id === "SELECTEDDEMERITKIND") return false;
            if (id === DEMERIT_NAME_DMY.toUpperCase()) return false;
            if (id === DEMERIT_HUDA_DMY.toUpperCase()) return false;
            return true;
        });
        const virtualCols = [
            buildDemeritKindColumn(sourceKind, carrierKind),
            buildDemeritMasterColumn(DEMERIT_NAME_DMY, "デメリットネーム", DEMERIT_KIND_NAME, sourceName),
            buildDemeritMasterColumn(DEMERIT_HUDA_DMY, "デメリット札", DEMERIT_KIND_HUDA, sourceHuda)
        ].filter((c) => !isEmpty(c));
        const safeIndex = Math.max(0, Math.min(insertIndex, base.length));
        return [
            ...base.slice(0, safeIndex),
            ...virtualCols,
            ...base.slice(safeIndex)
        ];
    }

    return {
        resolveDemeritKind,
        normalizeDemeritRows,
        applyDemeritKindToRow,
        syncDemeritDmtCd,
        normalizeDemeritRowForSave,
        buildDemeritGridColumns
    };
}
